import React, { useCallback } from 'react'
import { ListItem, ComboTooltip } from '@fs/zion-ui'
import { ContentLock, ContentEdit } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { getMostRecentFieldValue } from '@fs/zion-sls'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useCollectionContextProvider } from '../../../../providers/collectionProvider/CollectionProvider'
import { FIELDS } from '../../../../providers/collectionProvider/collectionConstants'
import { useR2DispatchContext } from '../../../../providers/r2Provider/R2Context'

/**
 * Share Field Button component renders a ListItem that represents a field shared between multiple persons.
 * It displays the field's label, and its most recent value. It also includes an edit icon that
 * triggers the onClick function when clicked.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {Object} props.field - The field object containing information about the form field.
 * @param {function} props.onClick - Function to handle the click event of the ListItem.
 * @param {Boolean} [props.readonly] - Flag to determine whether the field should be read only.
 *
 * @returns {JSX.Element} The rendered component
 */
const SharedFieldButton = ({ field, onClick, readonly = true }) => {
  const [t] = useTranslation()
  const { restrictedFields } = useCollectionContextProvider()
  const isRestricted = restrictedFields?.includes(FIELDS.NAME)
  const dispatch = useR2DispatchContext()
  const { openOverlay } = useOverlayContext()

  const sharedFieldTooltipLabel = t('edit.shared-field.tooltip-label', 'Linked Field')
  const sharedFieldTooltipDescription = t(
    'edit.shared-field.tooltip-description',
    'This field is temporarily disabled since the information connects many names together.'
  )
  const fieldDisplayLabel = field?.label?.toLowerCase()

  const getDisplayField = (parent, subElement) => {
    if (parent && !subElement) return parent
    return subElement
  }

  // RII specific functionality
  const handleClick = useCallback(() => {
    dispatch({ type: 'SET_ACTIVE_FIELD_ID', value: field?.id })
    dispatch({ type: 'SET_EDITING_SHARED_FIELD_ID', value: field?.id })
    openOverlay('editSharedFieldPage1')
  }, [dispatch, field?.id, openOverlay])

  return (
    <ListItem
      data-testid={field?.fieldType || field?.elementType}
      overline={field.label}
      primaryText={
        readonly
          ? getMostRecentFieldValue(field) || ''
          : getMostRecentFieldValue(getDisplayField(field, field?.subElements?.[0])) || ''
      }
      secondaryText={
        readonly
          ? t('edit.shared-field.readonly-caption', 'This information is connected to many names.')
          : t('edit.shared-field.caption', 'This information is connected to many names. Edit linked {fieldName}.', {
              fieldName: fieldDisplayLabel,
            })
      }
      endElement={
        readonly ? (
          <ComboTooltip
            label={sharedFieldTooltipLabel}
            description={sharedFieldTooltipDescription}
            onClick={() => {}}
            target={<ContentLock />}
          />
        ) : (
          <ListItem.Icon Icon={ContentEdit} />
        )
      }
      onClick={readonly ? () => undefined : onClick || handleClick}
      disabled={readonly || isRestricted}
    />
  )
}

export default SharedFieldButton
